import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle, Badge, Button } from '@goodday-clone/ui'
import { BadgeDollarSign, Kanban, Clock, Archive, RotateCcw, ArrowLeft } from 'lucide-react'
import { DealDetailModal, DealDetailData } from '@/components/sales/deal-detail-modal'
import { api } from '@/api/client'
import { getApiErrorMessage } from '@/shared/utils/apiError'
import { useDealStages } from '@/shared/hooks/useCrmVocabulary'

export default function DealsArchivePage() {
  const navigate = useNavigate()
  const { data: dealStages = [] } = useDealStages()
  const lostStageNames = dealStages.filter((st) => st.isLost).map((st) => st.name)
  const [archivedDeals, setArchivedDeals] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedDeal, setSelectedDeal] = useState<DealDetailData | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)

  const fetchArchivedDeals = async () => {
    setLoading(true)
    try {
      const data = await api.get<any>('/deals')
      const allDeals = Array.isArray(data) ? data : (data?.data || data?.deals || [])
      // Driven by the isLost flag on configured deal stages.
      const lost = allDeals.filter((d: any) => lostStageNames.includes(d.stage))
      if (lost.length > 0) {
        setArchivedDeals(
          lost.map((d: any) => ({
            id: d._id || d.id,
            title: d.title,
            client: d.client,
            stage: d.stage || 'Closed Lost',
            value: typeof d.value === 'number' ? d.value : parseFloat(d.value) || 0,
            reason: d.notes || 'Internal budget freeze',
            owner: d.owner || 'Alice Chen',
            closedAt: d.updatedAt ? new Date(d.updatedAt).toLocaleDateString() : 'Aug 20, 2026',
          }))
        )
      } else {
        setArchivedDeals([])
      }
    } catch {
      setArchivedDeals([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // dealStages loads asynchronously; re-run once it's actually in so
    // lostStageNames isn't permanently stuck at [] from the first render.
    fetchArchivedDeals()
  }, [dealStages])

  const handleRowClick = (dealItem: any) => {
    setSelectedDeal({
      id: dealItem.id,
      title: dealItem.title,
      client: dealItem.client,
      stage: dealItem.stage,
      value: dealItem.value,
      probability: 0,
      owner: dealItem.owner,
      notes: `Closed Lost reason: ${dealItem.reason}`,
      closeDate: dealItem.closedAt,
      organizationId: 'demo-org',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    })
    setIsDetailOpen(true)
  }

  const handleReopen = async (e: React.MouseEvent, dealItem: any) => {
    e.stopPropagation()
    try {
      await api.patch(`/deals/${dealItem.id}`, { stage: 'Proposal' }, { skipErrorToast: true })
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'reopen this deal'))
      return
    }
    setArchivedDeals((prev) => prev.filter((d) => d.id !== dealItem.id))
    toast.success(`Reopened "${dealItem.title}" to active pipeline!`)
  }

  return (
    <div className="space-y-6 max-w-full mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 transition-all hover:shadow-md hover:shadow-purple-300/50"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Deals Archive</h1>
            <p className="text-sm text-muted-foreground">Historical records of closed-lost opportunities and archived sales contracts</p>
          </div>
        </div>
      </div>

      {/* Subsections Navigation */}
      <div className="flex items-center gap-2 border-b border-border pb-3">
        <Link
          to="/app/deals"
          className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-1.5 transition-colors"
        >
          <BadgeDollarSign className="h-3.5 w-3.5" /> Active Deals
        </Link>
        <Link
          to="/app/deals/pipeline"
          className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-1.5 transition-colors"
        >
          <Kanban className="h-3.5 w-3.5" /> Pipeline
        </Link>
        <Link
          to="/app/deals/timeline"
          className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-1.5 transition-colors"
        >
          <Clock className="h-3.5 w-3.5" /> Timeline
        </Link>
        <Link
          to="/app/deals/archive"
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground shadow-sm flex items-center gap-1.5"
        >
          <Archive className="h-3.5 w-3.5" /> Archive
        </Link>
      </div>

      <Card>
        <CardHeader className="py-4 px-6 border-b border-border/50 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold">Archived Deals ({archivedDeals.length})</CardTitle>
          <Badge variant="secondary" className="text-xs">Archive</Badge>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border/50">
                <tr>
                  <th className="px-6 py-3">Deal Title</th>
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3">Lost Reason</th>
                  <th className="px-4 py-3">Value</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Closed Date</th>
                  <th className="px-6 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">Loading archive...</td>
                  </tr>
                ) : archivedDeals.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">No archived deals found.</td>
                  </tr>
                ) : (
                  archivedDeals.map((d) => (
                    <tr
                      key={d.id}
                      onClick={() => handleRowClick(d)}
                      className="hover:bg-muted/40 cursor-pointer transition-colors opacity-85 group"
                    >
                      <td className="px-6 py-3.5 font-semibold text-foreground group-hover:text-primary transition-colors">{d.title}</td>
                      <td className="px-4 py-3.5 text-muted-foreground">{d.client}</td>
                      <td className="px-4 py-3.5 text-xs text-destructive/80 font-medium">{d.reason}</td>
                      <td className="px-4 py-3.5 text-xs text-muted-foreground line-through">₹{d.value.toLocaleString()}</td>
                      <td className="px-4 py-3.5 text-xs text-muted-foreground">{d.owner}</td>
                      <td className="px-4 py-3.5 text-xs text-muted-foreground">{d.closedAt}</td>
                      <td className="px-6 py-3.5 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 gap-1 text-xs"
                          onClick={(e) => handleReopen(e, d)}
                        >
                          <RotateCcw className="h-3 w-3" /> Reopen
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <DealDetailModal
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        deal={selectedDeal}
        onDeleted={(id) => setArchivedDeals((prev) => prev.filter((d) => d.id !== id))}
        currentSectionName="Deals Archive"
        currentSectionHref="/app/deals/archive"
      />
    </div>
  )
}
